<script setup>
/**
 * Confirmations of completed actions, bottom right, dismissible.
 * Announced politely: they confirm, they never carry the only copy of an error.
 */
import { useToastStore } from '@/stores/toasts.js'

const toasts = useToastStore()
</script>

<template>
  <div class="toasts" role="status" aria-live="polite">
    <TransitionGroup name="toast">
      <div v-for="toast in toasts.items" :key="toast.id" class="toast" :class="`toast--${toast.tone}`">
        <span class="toast__message">{{ toast.message }}</span>
        <button type="button" class="toast__close" aria-label="Close" @click="toasts.dismiss(toast.id)">
          ×
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toasts {
  position: fixed;
  z-index: 70;
  right: var(--sp-6);
  bottom: var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  max-width: min(380px, calc(100vw - var(--sp-8)));
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-5);
  padding: var(--sp-4) var(--sp-4) var(--sp-4) var(--sp-6);
  background: var(--c-surface);
  border: 1px solid var(--c-border-strong);
  border-left: 3px solid var(--c-border-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  font-size: var(--text-md);
}

.toast--success {
  border-left-color: var(--st-ready-fg);
}

.toast--error {
  border-left-color: var(--c-danger);
}

.toast--info {
  border-left-color: var(--c-accent);
}

.toast__message {
  flex: 1;
}

.toast__close {
  flex: none;
  border: 0;
  background: transparent;
  color: var(--c-text-subtle);
  font-size: var(--text-lg);
  line-height: 1;
  cursor: pointer;
}

.toast__close:hover {
  color: var(--c-text);
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity var(--dur-med) var(--ease),
    transform var(--dur-med) var(--ease);
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
